import {
  AdditiveBlending,
  Color,
  DoubleSide,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
} from 'three';

// Cos celeste: billboard (pla que mira la càmera) amb un disc suau + halo additiu. Serveix tant
// per al SOL com per a la LLUNA (settings per instància). És independent del cel (la posició i la
// intensitat les porta TimeOfDay/Scene). Va al render de dalt i, com que és a l'escena, es
// reflecteix al mar automàticament (mirror del Water) → reflex que creix amb la intensitat.
//
// camera2D: es col·loca lluny en la direcció (elevació/azimut) i mira cap a la càmera (eix +Z).

export const SUN_BODY_SETTINGS = {
  color: '#cfe6ff',   // disc del sol (blanc-blavós → el grade el porta a clar #0165bb; és la font de llum)
  size: 360,          // mida del disc (unitats de món)
  brightness: 1.3,    // multiplicador de lluentor del disc
  core: 0.30,         // radi del nucli brillant (0..0.5 de la quad)
  glow: 3.6,          // caiguda del halo (més gran = halo més compacte)
  halo: 0.55,         // pes del halo respecte el nucli
  distance: 8000,     // distància a la qual es col·loca al cel
};

export const MOON_SETTINGS = {
  color: '#e7eeff',   // disc de la lluna (fred)
  size: 560,          // mida del disc
  brightness: 1.5,    // multiplicador de lluentor (lluna més brillant)
  core: 0.40,         // nucli brillant (cercle visible)
  glow: 3.0,          // caiguda del halo
  halo: 0.75,         // pes del halo
  distance: 8000,     // distància al cel
};

const VERTEX = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uIntensity, uCore, uGlow, uHalo;
  void main() {
    float d = length(vUv - 0.5) * 2.0;                 // 0 centre → 1 vora
    float core = smoothstep(uCore, uCore * 0.35, d);   // nucli brillant
    float halo = pow(clamp(1.0 - d, 0.0, 1.0), uGlow); // halo suau
    float a = (core + halo * uHalo) * uIntensity;
    if (a <= 0.001) discard;
    gl_FragColor = vec4(uColor * a, a);
  }
`;

export default class CelestialBody {
  constructor(settings) {
    this.s = settings;
    this.mesh = null;
    this.material = null;
    this._dir = new Vector3(0, 0.2, -1);
  }

  init() {
    const s = this.s;
    this.material = new ShaderMaterial({
      uniforms: {
        uColor: { value: new Color(s.color) },
        uIntensity: { value: 0 },
        uCore: { value: s.core },
        uGlow: { value: s.glow },
        uHalo: { value: s.halo },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide, // perquè es vegi també al reflex (mirror del mar)
    });
    this.mesh = new Mesh(new PlaneGeometry(s.size, s.size), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    return this.mesh;
  }

  // Col·loca el cos en una direcció unitària (elevació/azimut → vec3) i el fa mirar la càmera (+Z).
  setDirection(dir) {
    if (!this.mesh) return;
    this._dir.copy(dir).normalize();
    this.mesh.position.copy(this._dir).multiplyScalar(this.s.distance);
    this.mesh.lookAt(0, 0, 1500);
  }

  // Intensitat 0..1 (la modula la corba de dia/lluna) × brightness dels settings.
  setIntensity(v) {
    if (this.material) this.material.uniforms.uIntensity.value = Math.max(0, v) * this.s.brightness;
  }

  get direction() { return this._dir; }

  setConfig(partial = {}) {
    Object.assign(this.s, partial);
    if (this.material) {
      const u = this.material.uniforms, s = this.s;
      u.uColor.value.set(s.color);
      u.uCore.value = s.core;
      u.uGlow.value = s.glow;
      u.uHalo.value = s.halo;
    }
    if (this.mesh) {
      this.mesh.geometry.dispose();
      this.mesh.geometry = new PlaneGeometry(this.s.size, this.s.size);
      this.setDirection(this._dir);
    }
  }

  setLayer(n) { if (this.mesh) this.mesh.layers.set(n); }

  dispose() {
    this.mesh?.geometry?.dispose();
    this.material?.dispose();
    this.mesh = null;
    this.material = null;
  }

  get settings() { return this.s; }
}
